/**
 * @fileoverview OptimizationQueueContext - Provides optimization queue data across components
 * Prevents multiple hook instances and provides shared state
 * Also syncs completed optimization results with BacktestContext for Key Metrics updates
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useOptimizationQueue } from '../hooks/useOptimizationQueue';
import type { OptimizationJob } from '../services/optimization/OptimizationEventService';
import type { ApiTypes } from '../services/api/endpoints';

interface JobCounts {
    active: number;
    queued: number;
    completed: number;
}

export interface OptimizationQueueContextType {
    jobCounts: JobCounts;
    jobs: OptimizationJob[];
    activeJobs: OptimizationJob[];
    queuedJobs: OptimizationJob[];
    completedJobs: OptimizationJob[];
    selectedJobId: string | null;
    selectedJob: OptimizationJob | null;
    isConnected: boolean;
    loading: boolean;
    error: string | null;
    startOptimization: (request: ApiTypes.OptimizationRequest) => Promise<string>;
    cancelOptimization: (jobId: string) => Promise<void>;
    removeJob: (jobId: string) => Promise<void>;
    selectJob: (jobId: string | null) => void;
    refresh: () => Promise<void>;
}

export const OptimizationQueueContext = createContext<OptimizationQueueContextType | null>(null);

interface OptimizationQueueProviderProps {
    children: ReactNode;
}

export const OptimizationQueueProvider: React.FC<OptimizationQueueProviderProps> = ({
    children
}) => {
    const queueData = useOptimizationQueue({
        autoConnect: false, // Only connect when there are active jobs
        pollInterval: 10000
    });

    // Listen for WFA job start events and refresh the queue
    useEffect(() => {
        const handleWfaJobStarted = (event: CustomEvent) => {
            queueData.refresh().catch(console.error);
        };

        window.addEventListener('wfa-job-started', handleWfaJobStarted as EventListener);

        return () => {
            window.removeEventListener('wfa-job-started', handleWfaJobStarted as EventListener);
        };
    }, [queueData.refresh]);

    // Note: BacktestContext syncing is handled by OptimizationResultSyncer component
    // when used within a BacktestProvider

    const jobCounts: JobCounts = {
        active: queueData.activeJobs.length,
        queued: queueData.queuedJobs.length,
        completed: queueData.completedJobs.length
    };

    const selectedJob = queueData.completedJobs.find(job => job.id === queueData.selectedJobId) || null;

    const contextValue: OptimizationQueueContextType = {
        jobCounts,
        jobs: queueData.jobs,
        activeJobs: queueData.activeJobs,
        queuedJobs: queueData.queuedJobs,
        completedJobs: queueData.completedJobs,
        selectedJobId: queueData.selectedJobId,
        selectedJob,
        isConnected: queueData.connected,
        loading: queueData.loading,
        error: queueData.error,
        startOptimization: queueData.startOptimization,
        cancelOptimization: queueData.cancelOptimization,
        removeJob: queueData.removeJob,
        selectJob: queueData.selectJob,
        refresh: queueData.refresh
    };

    return (
        <OptimizationQueueContext.Provider value={contextValue}>
            {children}
        </OptimizationQueueContext.Provider>
    );
};

export const useOptimizationQueueContext = (): OptimizationQueueContextType => {
    const context = useContext(OptimizationQueueContext);
    if (!context) {
        throw new Error('useOptimizationQueueContext must be used within OptimizationQueueProvider');
    }
    return context;
};